import React from "react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { CircleData, Emblem3d, Step, StepOption } from "../../../../types/wizard";
import { useSnapshot } from "valtio";
import { saveEmblemData, setEmblem3d, wizardStore, validateWizardSteps } from "../../../../stores/wizard";
import { Steps } from "../../../../constants";
import useOutsideClick from "../../../../hooks/useOutsideClick";
import './../../QuickView.css';

interface IndicationManagerProps {
  step: Step;
}

const IndicationManager: FC<IndicationManagerProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const emblemData = wizSnap?.emblemData || {};
  const [savedStepOptions, setSavedStepOptions] = useState<StepOption[]>([]);
  const [savedCircleData, setSavedCircleData] = useState<CircleData[]>([]);
  const [indicators, setIndicators] = useState<number[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<(StepOption | null)[]>([]);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const [lastUpdatedIndex, setLastUpdatedIndex] = useState<number | null>(null);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const savedThreadColors = emblemData[Steps.ThreadColors]?.items || [];
  const dropdownRefs = useRef<Array<HTMLDivElement | null>>([]);
  const indexName = step?.id === Steps.Emblem3d ? "3D" : "Print Stitch";
  const stepProductOpt = wizSnap?.designRequest.productOptions[step?.id === Steps.Emblem3d ? "emblem3D" : "emblemPrintStitch"];
  const emblemProductOption = step?.id === Steps.Emblem3d
    ? stepProductOpt?.set3DEmblemOption
    : stepProductOpt?.setEmblemPrintStitchOption;
  const isInvalid = wizSnap.errors[step?.id]?.length;

  useEffect(() => {
    const defaultColor = { id: -1, name: "Other", htmlColor: "#FFFFFF" };
    const stepOptions: StepOption[] = savedThreadColors
      .filter((item: any) => !item.circle)
      .map((item: any) => item.option);

    const circleData: CircleData[] = savedThreadColors.filter((item: any) => item.circle);
    //@ts-ignore
    setSavedStepOptions([defaultColor, ...stepOptions]);
    setSavedCircleData(circleData);
  }, [wizSnap, emblemData]);

  useEffect(() => {
    if (lastUpdatedIndex !== null) {
      updateEmblemItem(lastUpdatedIndex);
    }
  }, [lastUpdatedIndex, selectedOptions, selectedDimensions]);

  useEffect(() => {
    const removedColors = selectedOptions
      .map((option, index) => ({ option, index }))
      .filter(
        ({ option }) =>
          option &&
          option.id !== -1 &&  // Ensure 'Other' option is not considered for removal
          !savedThreadColors.some(
            (tc: CircleData) =>
              tc.circle?.id === option.uniqueId && tc.option?.id === option.id
          )).map(({ index }) => index);

    if (removedColors.length > 0) {
      // Update the inputs by removing those that have deleted colors
      setIndicators(prev => prev.filter((_, i) => !removedColors.includes(i)));
      setSelectedOptions(prev => prev.filter((_, i) => !removedColors.includes(i)));
      setSelectedDimensions(prev => prev.filter((_, i) => !removedColors.includes(i)));

      // Optional: if 'emblemData' should also be updated
      const updatedItems = (emblemData[step.id]?.items || []).filter(
        (_: any, i: number) => !removedColors.includes(i)
      );
      saveEmblemData(step.id, { items: updatedItems });
      validateWizardSteps(step.id);
    }
  }, [selectedOptions, savedThreadColors]);

  const availableOptions = useMemo(() => {
    const combinedOptions = [
      ...savedStepOptions.map(option => ({ ...option, sourceType: "StepOption" })),
      ...savedCircleData.map(circle => ({
        ...circle.option,
        sourceType: "CircleData",
        circleData: circle,
        uniqueId: circle.circle?.id,
      })),
    ];

    return combinedOptions.filter(
      option =>
        option.id === -1 || !selectedOptions.some(selected => selected?.uniqueId === option.uniqueId)
    );
  }, [savedStepOptions, savedCircleData, selectedOptions]);

  const addInput = () => {
    setIndicators((prev) => [...prev, prev.length]);
    setSelectedOptions((prev) => [...prev, null]);
    setSelectedDimensions((prev) => [...prev, ""]);
  };

  const removeInput = (index: number) => {
    setIndicators((prev) => prev.filter((_, i) => i !== index));
    setSelectedOptions((prev) => prev.filter((_, i) => i !== index));
    setSelectedDimensions((prev) => prev.filter((_, i) => i !== index));
    if (openDropdownIndex === index) {
      setOpenDropdownIndex(null);
    }

    // Remove saved indication
    const currentItems = emblemData[step.id]?.items ?? [];
    const updatedItems = currentItems.filter((_: any, i: number) => i !== index);

    saveEmblemData(step.id, { items: updatedItems });
    validateWizardSteps(step.id);
  };

  const handleSelect = (dropdownIndex: number, selectedOption: StepOption, selectorIndex: number) => {
    setSelectedOptions((prev) =>
      prev.map((option, i) => (i === dropdownIndex ? selectedOption : option))
    );
    setSelectedDimensions((prev) =>
      prev.map((level, i) =>
        i === selectorIndex ? (level || "High") : level
      )
    );

    setOpenDropdownIndex(null);
    updateEmblemItem(dropdownIndex);
  };

  const handleLevelChange = (index: number, level: string) => {
    setSelectedDimensions((prev) =>
      prev.map((selectedDimension, i) => (i === index ? level : selectedDimension))
    );
    setLastUpdatedIndex(index);
  };

  const handleSpecialInstructions = (event: React.ChangeEvent<HTMLTextAreaElement>, index: number) => {
    const specialInstructions = event.target.value;
    updateEmblemItem(index, specialInstructions);
  }

  const updateEmblemItem = (
    index: number,
    specialInstructions?: string
  ) => {
    const option = selectedOptions[index];
    const dimension = selectedDimensions[index];
    const stepId = step?.id;

    if (!option || (!dimension && stepId === Steps.Emblem3d)) return;

    const currentItems = emblemData[step.id]?.items ?? [];
    const updatedItem: CircleData = {
      stepId: Steps.ThreadColors,
      option: option ?? (currentItems[index]?.option ?? {} as StepOption),
      //@ts-ignore
      circle: option?.circleData?.circle ?? undefined,
      //@ts-ignore
      color: option?.circleData?.color ?? option?.htmlColor,
      dimension: selectedDimensions[index],
      instructions: specialInstructions ?? "",
    };

    const updatedItems = [...currentItems];
    updatedItems[index] = updatedItem;

    const indicators = updatedItems.map((item) => ({
      colorClientId: item.option?.clientId ?? -1,
      x: item.circle?.x ?? null,
      y: item.circle?.y ?? null,
      indicator: item.dimension ?? "High",
      instructions: item.instructions ?? null,
    }));

    const emblem3d: Emblem3d = {
      indicators
    };

    setEmblem3d(emblem3d);
    saveEmblemData(step.id, { items: updatedItems });
    validateWizardSteps(step.id);
  };

  useOutsideClick(
    dropdownRefs.current.map((ref) => ({ current: ref })),
    () => setOpenDropdownIndex(null)
  );

  return (<>
    {emblemProductOption === "AllowWEIToChoose" ? null
      : (<>
        <div className="relative rounded bg-white">
          <div className="flex flex-col justify-left">
            {indicators.map((_, index) => {
              const borderColor: boolean = !!(selectedOptions[index]?.htmlColor && selectedOptions[index]?.htmlColor === "#FFFFFF");

              return <React.Fragment key={index}>
                <div className="flex flex-col space-y-2">
                  {/* Selector */}
                  <div
                    ref={(el) => (dropdownRefs.current[index] = el)}
                    className="w-full mt-1">
                    <label className="block uppercase font-bold mb-2.5">{`${indexName} Indication ${index + 1}`}</label>

                    {/* Radio Buttons High/Low for 3D Emblem */}
                    {step?.id === Steps.Emblem3d &&
                      <div className="gap-4 radio-button">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`level-${index}`}
                            value="High"
                            checked={selectedDimensions[index] === "High"}
                            onChange={() => handleLevelChange(index, "High")}
                          />
                          High
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`level-${index}`}
                            value="Low"
                            checked={selectedDimensions[index] === "Low"}
                            onChange={() => handleLevelChange(index, "Low")}
                          />
                          Low
                        </label>
                      </div>
                    }

                    <div
                      className="p-2.5 cursor-pointer relative rounded bg-white"
                      style={{ border: "1px solid #ccc" }}
                      onClick={() => setOpenDropdownIndex(index)}
                    >
                      {selectedOptions[index] ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div
                            className="w-8 h-8 rounded"
                            style={{
                              backgroundColor: selectedOptions[index]?.htmlColor || "",
                              border: `${borderColor ? '1px solid #a7acaf' : 'none'}`,
                            }} />
                          <span>{selectedOptions[index]?.name}</span>
                        </div>
                      ) : (
                        <div className="flex flex-row justify-between items-center">
                          <span style={{ display: "flex" }}>{indexName} Indication</span>
                          <div className="arrow"></div>
                        </div>
                      )}
                    </div>

                    {/* Selector options */}
                    {openDropdownIndex === index && (
                      <div className="relative w-full">
                        <ul
                          className="m-0 p-0 rounded bg-white overflow-y-auto absolute"
                          style={{
                            listStyle: "none",
                            border: "1px solid #ccc",
                            maxHeight: "200px",
                            width: "inherit",
                            zIndex: 1,
                          }}
                        >
                          {availableOptions.map((option, i) => {
                            const borderColor: boolean = !!(option?.htmlColor && option.htmlColor === "#FFFFFF");

                            return <li
                              key={`circle-${option.id}-${i}`}
                              onClick={() => handleSelect(index, option, i)}
                              className="flex items-center p-2 cursor-pointer"
                              style={{
                                gap: "10px",
                                borderBottom: "1px solid #eee",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor = "#f5f5f5")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor = "transparent")
                              }>
                              <div
                                className="w-8 h-8 rounded"
                                style={{
                                  backgroundColor: option.htmlColor || "",
                                  border: `${borderColor ? '1px solid #a7acaf' : 'none'}`,
                                }} />
                              <span>{option.name}</span>
                            </li>
                          })}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Remove input btn */}
                  <div className="flex flex-row justify-end">
                    <button
                      className="outline-none hover:outline-none border-0 underline text-sm text-error px-0 py-1 bg-inherit"
                      onClick={() => removeInput(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Special Instructions */}
                <div className='w-full flex flex-col items-start gap-2 mt-2 mb-4'>
                  <h2 className="">
                    <span className="font-bold uppercase mr-2">Special Instructions</span>
                    <span>(Required)</span>
                  </h2>
                  <textarea
                    className='border w-full rounded h-24 p-1 outline-none'
                    style={{ border: "1px solid #ccc" }}
                    onChange={(e) => handleSpecialInstructions(e, index)}
                    placeholder={step?.specialInstructionsPlaceholder || 'Please provide additional, detailed instructions here to help with the production of your design.'}
                    value={emblemData[step?.id]?.specialInstruction}
                  />
                </div>
              </React.Fragment>
            })}
            <div className="flex flex-col justify-left py-4">
              <div className="flex flex-row">
                <button
                  className="btn-file bg-black text-white underline rounded"
                  onClick={() => addInput()}>
                  {step?.id === Steps.Emblem3d ? "Add New 3D Indication" : "Add New Indication"}
                </button>
              </div>
            </div>
          </div>
        </div>
        {isInvalid &&
          <div className="text-[#a61613] text-sm leading-4 mt-1">
            <span>{wizSnap.errors[step?.id][0]}</span>
          </div>
        }
      </>)}
  </>)
};

export default IndicationManager;